import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Container,
  CircularProgress,
  Typography,
  Alert,
} from '@mui/material';
import apiService from '../services/api';

const CallbackPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState<string | null>(null);
  const isProcessingRef = useRef(false);

  useEffect(() => {
    const handleCallback = async () => {
      // Prevent double execution (React StrictMode or multiple renders)
      if (isProcessingRef.current) {
        return;
      }

      const code = searchParams.get('code');
      const errorParam = searchParams.get('error');


      if (errorParam) {
        const errorDesc = searchParams.get('error_description') || errorParam;
        setError(`Authentication error: ${errorDesc}`);
        return;
      }

      if (!code) {
        setError('Authorization code not found');
        return;
      }

      // Mark as processing to prevent duplicate calls
      isProcessingRef.current = true;

      try {
        // Exchange code for token
        const authResponse = await apiService.handleAuthCallback(code);
        
        
        // Store token
        localStorage.setItem('access_token', authResponse.access_token);

        if (authResponse.refresh_token) {
          localStorage.setItem('refresh_token', authResponse.refresh_token);
        }
        
        // Store token expiration time for session warning
        if (authResponse.expires_in) {
          const expiresAt = Date.now() + (authResponse.expires_in * 1000);
          localStorage.setItem('token_expires_at', expiresAt.toString());
        }
        
        // Fetch user info to determine redirect location based on role
        try {
          const userData = await apiService.getCurrentUser();
          
          // Redirect based on user role
          if (userData.roles && userData.roles.includes('student')) {
            window.location.href = '/itinerary';
          } else {
            window.location.href = '/dashboard';
          }
        } catch (userErr) {
          window.location.href = '/dashboard';
        }
      } catch (err: any) {
        
        const errorDetail = err.response?.data?.detail || err.message || 'Authentication failed';
        
        // Don't auto-redirect - stay on error page so we can see it
        setError(errorDetail);
        
        // Also store in localStorage so we can check it
        localStorage.setItem('last_auth_error', JSON.stringify({
          detail: errorDetail,
          status: err.response?.status,
          timestamp: new Date().toISOString()
        }));
      }
    };

    handleCallback();
  }, [searchParams, navigate]); // Only depend on searchParams and navigate

  if (error) {
    return (
      <Container maxWidth="sm">
        <Box
          sx={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Alert severity="error" sx={{ width: '100%' }}>
            <Typography variant="h6" gutterBottom>
              Authentication Failed
            </Typography>
            <Typography>{error}</Typography>
          </Alert>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
        }}
      >
        <CircularProgress size={60} sx={{ mb: 2 }} />
        <Typography variant="h6" gutterBottom>
          Completing Sign In...
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Please wait while we verify your credentials.
        </Typography>
      </Box>
    </Container>
  );
};

export default CallbackPage;
